import React from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import Select from '../../../components/widgets/Select';
import { Toggle, WarningMessage } from '../../../components/widgets';
import QuestionMark from '../../../components/widgets/QuestionMark';
import Label from '../../../components/foundations/Label';
import FormInnerSection from '../FormInnerSection';
import Input from '../Input';
import {
    ATTRIBUTION_TAB_IDS,
    POSTBACK_EVENT_TYPE_OPTIONS,
    POSTBACK_METHOD_OPTIONS,
    POSTBACK_METHODS,
    TRANSLATE_PREFIX,
} from '../../consts';
import useConfirmDialog from '../../useConfirmDialog';
import { urlField, useFieldValidityContext } from '../../useFieldValidity';
import { useTabUpdater } from '../../formUpdaters';
import css from './PostbackUrlBaseSection.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;

const PostbackUrlBaseSection = ({ translate, readOnly = false }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(TAB_ID);
    const tabData = values.formData?.[TAB_ID] || {};
    const { confirm, dialog } = useConfirmDialog();

    const eventsAndPlatforms = tabData.postback_events_and_platforms || [];
    const selectedEventTypes = eventsAndPlatforms.filter(e => (e.platforms || []).length > 0).map(e => e.event_type);
    const hasAnySelection = selectedEventTypes.length > 0;

    const method = tabData.postback_method ?? POSTBACK_METHODS.GET;
    const differentDomains = !!tabData.different_domains_per_postback;

    const setField = (field, value) => updateTab({ [field]: value });

    const methodOptions = POSTBACK_METHOD_OPTIONS.map(m => ({ value: m, label: m }));
    const selectedMethod = methodOptions.find(o => o.value === method) || methodOptions[0];

    const hasAnyPerEventUrl = () => POSTBACK_EVENT_TYPE_OPTIONS.some(et => !!tabData[urlField(et)]?.trim());

    const clearPerEventUrls = extraTabFields =>
        updateTab(prevTab => {
            const nextTab = { ...prevTab, ...extraTabFields };
            POSTBACK_EVENT_TYPE_OPTIONS.forEach(et => {
                nextTab[urlField(et)] = undefined;
            });
            return nextTab;
        });

    const handleDifferentDomainsToggle = e => {
        const { checked } = e.target;
        if (!checked && hasAnyPerEventUrl()) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_DIFFERENT_DOMAINS_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_DIFFERENT_DOMAINS_TEXT`,
                onConfirm: () => clearPerEventUrls({ different_domains_per_postback: false }),
            });
            return;
        }
        setField('different_domains_per_postback', checked);
    };

    const apiValid = useFieldValidityContext();

    const shouldShowUrlError = (field, trimmedValue) => !readOnly && !!trimmedValue && apiValid[field] === false;

    const baseUrlInvalid = shouldShowUrlError('postback_url_base', tabData.postback_url_base?.trim());
    const eventUrlInvalid = eventType => {
        const key = urlField(eventType);
        return shouldShowUrlError(key, tabData[key]?.trim());
    };
    const urlHint = translate(`${TRANSLATE_PREFIX}.ERRORS.POSTBACK_URL_INVALID_HINT`);

    return (
        <FormInnerSection
            titleKey={`${TAB_PREFIX}.POSTBACK_URL_BASE_LABEL`}
            descriptionKey={`${TAB_PREFIX}.POSTBACK_URL_BASE_SUBTITLE`}
        >
            <div className={css.methodUrlRow}>
                <Select
                    options={methodOptions}
                    value={selectedMethod}
                    onChange={opt => setField('postback_method', opt ? opt.value : POSTBACK_METHODS.GET)}
                    isSearchable={false}
                    isClearable={false}
                    isDisabled={readOnly}
                    className={css.methodSelect}
                />
                <Input
                    value={tabData.postback_url_base || ''}
                    placeholder={translate(`${TAB_PREFIX}.POSTBACK_URL_BASE_PLACEHOLDER`)}
                    onChange={val => setField('postback_url_base', val)}
                    disabled={readOnly}
                    containerClass={css.urlBaseInput}
                    error={baseUrlInvalid ? urlHint : undefined}
                    required={!readOnly}
                />
            </div>
            {method === POSTBACK_METHODS.POST && (
                <WarningMessage
                    type="info"
                    className={css.postBodyInfo}
                    showIcon={false}
                    message={
                        <div className={css.postBodyContent}>
                            <div className={css.postBodyLabel}>
                                <Translate id={`${TAB_PREFIX}.POST_BODY_LABEL`} />
                                <QuestionMark message={`${TAB_PREFIX}.POST_BODY_TOOLTIP`} />
                            </div>
                            <div className={css.postBodyExplanation}>
                                <Translate id={`${TAB_PREFIX}.POST_BODY_EXPLANATION`} />
                            </div>
                            <code className={css.postBodyExample}>
                                <Translate id={`${TAB_PREFIX}.POST_BODY_EXAMPLE`} />
                            </code>
                        </div>
                    }
                />
            )}
            <FormInnerSection
                titleKey={`${TAB_PREFIX}.DIFFERENT_DOMAINS_LABEL`}
                descriptionKey={`${TAB_PREFIX}.DIFFERENT_DOMAINS_SUBTITLE`}
                disabledMessageKey={
                    !readOnly && !hasAnySelection ? `${TAB_PREFIX}.DIFFERENT_DOMAINS_DISABLED_MESSAGE` : undefined
                }
                headerRight={
                    <Toggle
                        checked={differentDomains}
                        onToggle={handleDifferentDomainsToggle}
                        disabled={readOnly || !hasAnySelection}
                    />
                }
            >
                {differentDomains &&
                    selectedEventTypes.map(eventType => (
                        <div key={eventType} className={css.eventUrlField}>
                            <Label
                                text={`${TAB_PREFIX}.EVENT_TYPES.${eventType.toUpperCase()}`}
                                type="p"
                                className={css.eventUrlLabel}
                            />
                            <Input
                                value={tabData[urlField(eventType)] || ''}
                                placeholder={translate(
                                    `${TAB_PREFIX}.POSTBACK_URL_PLACEHOLDERS.${eventType.toUpperCase()}`
                                )}
                                onChange={val => setField(urlField(eventType), val)}
                                disabled={readOnly}
                                containerClass={css.eventUrlInput}
                                error={eventUrlInvalid(eventType) ? urlHint : undefined}
                            />
                        </div>
                    ))}
            </FormInnerSection>
            {dialog}
        </FormInnerSection>
    );
};

PostbackUrlBaseSection.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(PostbackUrlBaseSection);
